import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import './App.css';

function App() {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseText, setCaseText] = useState('');
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const getTruncatedSnippet = (text, maxLength = 150) => {
    if (!text) return "No snippet available.";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  const handleInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.trim() === '') {
      setResults([]);
      setError('');
    }
  };

  const handleBackToResults = () => {
    setSelectedCase(null);
    setCaseText(''); 
    setAnalysisLoading(false);
    window.scrollTo(0, 0); 
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || searchQuery.trim().length < 2) return;

    setLoading(true);
    setError('');
    setResults([]); 
    setSelectedCase(null);
    setCaseText('');
    window.scrollTo(0, 0);

    try {
      const response = await fetch(
        `https://0iu2gakji3.execute-api.us-east-2.amazonaws.com/prod/cases?query=${encodeURIComponent(searchQuery)}`
      );
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const data = await response.json();
      const finalData = (data && data.body !== undefined) 
        ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) 
        : data;

      setResults(Array.isArray(finalData) ? finalData : []);
      if (finalData.length === 0) setError('No cases found.');
    } catch (err) {
      setError('Failed to fetch cases.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCaseText = async () => {
    const opinionId = selectedCase?.opinions?.[0]?.id;
    if (!opinionId) return alert("No opinion ID found.");

    setAnalysisLoading(true);
    setCaseText(''); 

    try {
      const response = await fetch(
        `https://0iu2gakji3.execute-api.us-east-2.amazonaws.com/prod/summarize`, 
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ opinionId: opinionId })
        }
      );

      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const data = await response.json();
      const parsedBody = (data && data.body !== undefined) 
        ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) 
        : data;

      setCaseText(parsedBody.text || "No summary available.");
    } catch (err) {
      setCaseText('Error: Failed to generate summary.');
    } finally {
      setAnalysisLoading(false);
    }
  };

  return (
    <div className="App">
      <div className="search-container">
        <h1>Case Law Search</h1>
        {!selectedCase && (
          <form onSubmit={handleSubmit} className="search-form">
            <input
              type="text"
              onChange={handleInputChange} 
              value={searchQuery}
              className="search-input"
              placeholder="Search legal cases..."
            />
            <button type="submit" className="submit-button">Search</button>
          </form>
        )}

        {loading && <p className="status-msg">Searching Database...</p>}
        {error && <p className="error-msg">{error}</p>}

        {!selectedCase ? (
          <div className="results-grid">
            {results.map((caseItem) => (
              <div key={caseItem.cluster_id} className="case-tile">
                <div className="tile-header">
                  <h3>{caseItem.caseName || 'Unnamed Case'}</h3>
                  <span className="case-date">{caseItem.dateFiled}</span>
                </div>
                <p className="case-court">{caseItem.court}</p>
                <div className="tile-snippet">
                  <p>{getTruncatedSnippet(caseItem.opinions?.[0]?.snippet)}</p>
                </div>
                <button onClick={() => setSelectedCase(caseItem)} className="summary-button">
                  View Details
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="summary-view">
            <button onClick={handleBackToResults} className="back-button">← Back to Results</button>
            
            <section className="case-header-details">
              <h2>{selectedCase.caseName}</h2>
              <div className="case-meta">
                <p><strong>Court:</strong> {selectedCase.court}</p>
                <p><strong>Date Filed:</strong> {selectedCase.dateFiled}</p>
                {selectedCase.absolute_url && (
                  <a href={`https://www.courtlistener.com${selectedCase.absolute_url}`} target="_blank" rel="noreferrer">
                    View Official Record
                  </a>
                )}
              </div>
            </section>

            <hr className="divider" />

            <section className="case-analysis-section">
              <div className="analysis-button-grid">
                <button className="analysis-btn" onClick={fetchCaseText} disabled={analysisLoading}>
                  <span className="btn-title">
                    <span className="icon">⚖️</span>
                    {analysisLoading ? "Analyzing Case..." : "Generate AI Summary"}
                  </span>
                  <span className="btn-desc">Summarize the core legal arguments and holding.</span>
                </button>
              </div>

              <p className="ai-disclaimer">
                <strong>Disclaimer:</strong> This summary is generated by AI for informational purposes and should not be considered legal advice.
              </p>

              {caseText && (
                <div className="analysis-output fadeIn">
                   <h4>AI Legal Brief</h4>
                   <div className="markdown-summary">
                     <ReactMarkdown>{caseText}</ReactMarkdown>
                   </div>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;